import { calculateIndicator, type IndicatorPoint } from "@/lib/chart/indicators"
import { formatNumber, formatPrice, formatTimeLabel, timeTicks } from "@/lib/chart/format"
import {
  clearSurface,
  drawCrosshair,
  drawEmptyState,
  drawGrid,
  drawLine,
  drawPaneLabel,
  drawTimeAxis,
  drawValueAxis,
  plotRect,
  TIME_TICK_SPACING,
  type PlotRect,
} from "@/lib/chart/primitives"
import { extent, linearScale } from "@/lib/chart/scales"
import { CHART_COLORS, type ChartTheme } from "@/lib/chart/theme"
import type { ChartOverlay } from "@/types/chartOverlays"
import type { ChartMode, FuturesBar, FuturesChartPreferences, IndicatorKey } from "@/types/futuresChart"

export type { PlotRect }

export const CHART_LEFT_PADDING = 12
export const CHART_RIGHT_PADDING = 70
export const CHART_TOP_PADDING = 16
const TIME_AXIS_HEIGHT = 28

interface IndicatorSeries {
  key: IndicatorKey
  points: IndicatorPoint[]
}

interface LowerPane {
  kind: "volume" | "open_interest"
  rect: PlotRect
}

export interface FinancialChartLayout {
  main: PlotRect
  panes: LowerPane[]
  /** Bottom of the last pane; the time axis sits below it. */
  plotBottom: number
  visible: FuturesBar[]
  indicatorSeries: IndicatorSeries[]
  priceScale: ReturnType<typeof linearScale>
  x: (index: number) => number
  indexForTime: (time: string) => number | null
}

interface LayoutInput {
  width: number
  height: number
  bars: FuturesBar[]
  start: number
  end: number
  preferences: FuturesChartPreferences
}

// Indicators only depend on the bar array, so cache them per array instead of
// recomputing every series on each hover redraw.
const indicatorCache = new WeakMap<FuturesBar[], Map<IndicatorKey, IndicatorPoint[]>>()

function cachedIndicator(bars: FuturesBar[], key: IndicatorKey): IndicatorPoint[] {
  let byKey = indicatorCache.get(bars)
  if (!byKey) {
    byKey = new Map()
    indicatorCache.set(bars, byKey)
  }
  let points = byKey.get(key)
  if (!points) {
    points = calculateIndicator(bars, key)
    byKey.set(key, points)
  }
  return points
}

/** Drawn candle/OHLC width for `count` bars across a plot `plotWidth` pixels wide. */
export function candleWidthFor(plotWidth: number, count: number): number {
  return Math.max(1, Math.min(12, (plotWidth / Math.max(1, count)) * 0.68))
}

/** Indicator value at an absolute bar index (cached per bar array). */
export function indicatorValueAt(bars: FuturesBar[], key: IndicatorKey, index: number): number | null {
  return cachedIndicator(bars, key)[index]?.value ?? null
}

export function buildFinancialChartLayout(input: LayoutInput): FinancialChartLayout | null {
  const { width, height, bars, start, end, preferences } = input
  const visible = bars.slice(start, end)
  if (visible.length === 0) return null

  const paneKinds: LowerPane["kind"][] = []
  if (preferences.show_volume && visible.some((bar) => bar.volume != null)) paneKinds.push("volume")
  if (preferences.show_open_interest && visible.some((bar) => bar.open_interest != null)) paneKinds.push("open_interest")
  const paneHeight = paneKinds.length > 0 ? Math.min(100, height * 0.18) : 0
  const plotBottom = height - TIME_AXIS_HEIGHT
  const mainBottom = plotBottom - paneHeight * paneKinds.length
  const main = plotRect(width, CHART_LEFT_PADDING, CHART_RIGHT_PADDING, CHART_TOP_PADDING, mainBottom)
  const panes = paneKinds.map((kind, index) => {
    const top = mainBottom + paneHeight * index
    return { kind, rect: plotRect(width, CHART_LEFT_PADDING, CHART_RIGHT_PADDING, top, top + paneHeight) }
  })

  const indicatorSeries = preferences.indicators.map((key) => ({
    key,
    points: cachedIndicator(bars, key).slice(start, end),
  }))
  // Overlay prices (alerts, drawings) are deliberately left out of the domain so a
  // far-away level cannot squash the candles; drawOverlays pins those to the edge.
  const values: number[] = []
  for (const bar of visible) values.push(bar.low, bar.high)
  for (const { points } of indicatorSeries) {
    for (const point of points) if (point.value !== null) values.push(point.value)
  }
  const [min, max] = extent(values) ?? [0, 1]
  const padding = Math.max((max - min) * 0.08, Math.abs(max) * 0.002, 0.01)
  const priceScale = linearScale([min - padding, max + padding], [main.bottom, main.top])
  const x = (index: number): number => main.left + ((index + 0.5) / visible.length) * main.width

  const indexForTime = (time: string): number | null => {
    const absolute = bars.findIndex((bar) => bar.time === time)
    if (absolute < start || absolute >= end) return null
    return absolute - start
  }

  return { main, panes, plotBottom, visible, indicatorSeries, priceScale, x, indexForTime }
}

/**
 * Left edge of a drawn level. A ray starts at the first bar on or after its origin,
 * so a daily origin still anchors once bars are aggregated to weekly (week-ending) times.
 */
export function overlayStartX(overlay: ChartOverlay, layout: FinancialChartLayout, bars: FuturesBar[], start: number): number {
  if (overlay.kind !== "horizontal_ray" || !overlay.origin_time) return layout.main.left
  const absolute = bars.findIndex((bar) => bar.time >= overlay.origin_time!)
  if (absolute === -1) return layout.main.right
  if (absolute < start) return layout.main.left
  return Math.min(layout.main.right, layout.x(absolute - start))
}

interface RenderOptions {
  ctx: CanvasRenderingContext2D
  width: number
  height: number
  bars: FuturesBar[]
  start: number
  end: number
  preferences: FuturesChartPreferences
  hoverIndex: number | null
  /** Pointer y in CSS pixels, for the horizontal crosshair and its price label. */
  hoverY?: number | null
  overlays?: ChartOverlay[]
  theme?: ChartTheme
}

export function renderFinancialChart(options: RenderOptions): void {
  const { ctx, width, height, bars, start, end, preferences, hoverIndex, hoverY = null, overlays = [] } = options
  const theme = options.theme ?? CHART_COLORS
  clearSurface(ctx, theme, width, height)

  const layout = buildFinancialChartLayout({ width, height, bars, start, end, preferences })
  if (!layout) {
    drawEmptyState(ctx, theme, width, height, "No daily bars available for this contract")
    return
  }

  const { main, visible, indicatorSeries, priceScale, x } = layout
  const priceTicks = priceScale.ticks(Math.max(3, Math.floor(main.height / 60)))
  const priceStep = priceTicks.length > 1 ? priceTicks[1] - priceTicks[0] : undefined
  const ticks = timeTicks(
    visible.map((bar) => bar.time),
    Math.floor(main.width / TIME_TICK_SPACING),
  )
  drawGrid(ctx, theme, main, priceTicks, priceScale.map, ticks.map((tick) => x(tick.index)))

  ctx.save()
  ctx.beginPath()
  ctx.rect(main.left, main.top, main.width, main.height)
  ctx.clip()
  drawPriceSeries(ctx, theme, visible, preferences.mode, x, priceScale.map, main.width)
  for (const { key, points } of indicatorSeries) {
    drawLine(
      ctx,
      points.map((point, index) => ({
        x: x(index),
        y: point.value === null ? null : priceScale.map(point.value),
      })),
      theme.indicators[key],
      1.5,
    )
  }
  ctx.restore()
  drawOverlays(ctx, theme, overlays, layout, bars, start)

  drawValueAxis(ctx, theme, main, priceTicks, priceScale.map, (value) =>
    formatNumber(value, { step: priceStep, compact: false }),
  )
  drawTimeAxis(ctx, theme, { ...main, bottom: layout.plotBottom }, ticks, x)

  for (const pane of layout.panes) {
    if (pane.kind === "volume") drawVolumePane(ctx, theme, visible, pane.rect, x)
    else drawOpenInterestPane(ctx, theme, visible, pane.rect, x)
  }

  if (hoverIndex !== null && hoverIndex >= start && hoverIndex < end) {
    const localIndex = hoverIndex - start
    drawCrosshair(ctx, theme, {
      x: x(localIndex),
      y: hoverY,
      plot: main,
      bottom: layout.plotBottom,
      width,
      timeLabel: formatTimeLabel(visible[localIndex].time),
      valueLabel: hoverY === null ? null : formatPrice(priceScale.invert(hoverY)),
    })
  }
}

export function priceFromClientY(
  clientY: number,
  canvas: HTMLCanvasElement,
  layout: FinancialChartLayout,
): number | null {
  const bounds = canvas.getBoundingClientRect()
  const y = clientY - bounds.top
  if (y < layout.main.top || y > layout.main.bottom) return null
  return layout.priceScale.invert(y)
}

function drawOverlays(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  overlays: ChartOverlay[],
  layout: FinancialChartLayout,
  bars: FuturesBar[],
  start: number,
): void {
  const { main } = layout
  ctx.font = "10px ui-monospace, SFMono-Regular, monospace"
  ctx.textBaseline = "middle"
  overlays.forEach((overlay) => {
    const rawY = layout.priceScale.map(overlay.price)
    const color = overlay.color ?? (overlay.source === "alert" ? theme.alertLine : theme.drawingLine)
    ctx.fillStyle = color

    // Levels outside the visible price range get an edge marker instead of a line.
    if (rawY < main.top || rawY > main.bottom) {
      const above = rawY < main.top
      const y = above ? main.top + 6 : main.bottom - 6
      const tipX = main.right + 4
      ctx.beginPath()
      ctx.moveTo(tipX, above ? y - 5 : y + 5)
      ctx.lineTo(tipX - 4, above ? y + 1 : y - 1)
      ctx.lineTo(tipX + 4, above ? y + 1 : y - 1)
      ctx.closePath()
      ctx.fill()
      // Label inside the plot so it can't collide with the price-axis ticks.
      ctx.textAlign = "right"
      ctx.fillText(formatPrice(overlay.price), main.right - 4, y)
      ctx.textAlign = "start"
      return
    }

    const dashed = overlay.source === "ai_suggested" || overlay.source === "ai_chat"
    ctx.strokeStyle = color
    ctx.lineWidth = overlay.selected ? 2.5 : 1.5
    if (dashed) ctx.setLineDash([6, 4])
    ctx.beginPath()
    ctx.moveTo(overlayStartX(overlay, layout, bars, start), rawY)
    ctx.lineTo(main.right, rawY)
    ctx.stroke()
    ctx.setLineDash([])

    ctx.fillText(formatPrice(overlay.price), main.right + 8, rawY)
    if (overlay.label) {
      ctx.fillText(overlay.label, main.left + 4, rawY - 8)
    }
  })
}

function drawPriceSeries(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  bars: FuturesBar[],
  mode: ChartMode,
  x: (index: number) => number,
  y: (value: number) => number,
  width: number,
): void {
  if (mode === "line") {
    drawLine(
      ctx,
      bars.map((bar, index) => ({ x: x(index), y: y(bar.close) })),
      theme.line,
      2,
    )
    return
  }

  const candleWidth = candleWidthFor(width, bars.length)
  bars.forEach((bar, index) => {
    const center = x(index)
    const color = bar.close >= bar.open ? theme.up : theme.down
    ctx.strokeStyle = color
    ctx.fillStyle = color
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(center, y(bar.high))
    ctx.lineTo(center, y(bar.low))
    ctx.stroke()

    if (mode === "ohlc") {
      ctx.beginPath()
      ctx.moveTo(center - candleWidth / 2, y(bar.open))
      ctx.lineTo(center, y(bar.open))
      ctx.moveTo(center, y(bar.close))
      ctx.lineTo(center + candleWidth / 2, y(bar.close))
      ctx.stroke()
    } else {
      const top = Math.min(y(bar.open), y(bar.close))
      const bodyHeight = Math.max(1.5, Math.abs(y(bar.open) - y(bar.close)))
      ctx.fillRect(center - candleWidth / 2, top, candleWidth, bodyHeight)
    }
  })
}

function drawVolumePane(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  bars: FuturesBar[],
  pane: PlotRect,
  x: (index: number) => number,
): void {
  const max = Math.max(extent(bars.map((bar) => bar.volume ?? 0))?.[1] ?? 0, 1)
  const barWidth = Math.max(1, Math.min(8, (pane.width / bars.length) * 0.6))
  ctx.fillStyle = theme.volume
  bars.forEach((bar, index) => {
    const height = ((bar.volume ?? 0) / max) * (pane.height - 18)
    ctx.fillRect(x(index) - barWidth / 2, pane.bottom - height, barWidth, height)
  })
  drawPaneLabel(ctx, theme, pane, "VOLUME", formatNumber(max))
}

function drawOpenInterestPane(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  bars: FuturesBar[],
  pane: PlotRect,
  x: (index: number) => number,
): void {
  const range = extent(bars.flatMap((bar) => (bar.open_interest == null ? [] : [bar.open_interest])))
  if (!range) return
  const scale = linearScale(range, [pane.bottom - 5, pane.top + 18])
  drawLine(
    ctx,
    bars.map((bar, index) => ({
      x: x(index),
      y: bar.open_interest == null ? null : scale.map(bar.open_interest),
    })),
    theme.openInterest,
    1.5,
  )
  drawPaneLabel(ctx, theme, pane, "OPEN INTEREST", formatNumber(range[1]))
}
